import { buildApiUrl } from '../config/api'

export interface AmazonProduct {
  id: string
  title: string
  name?: string
  description: string
  price: number
  originalPrice?: number
  currency: string
  image: string
  rating: number
  reviewCount: number
  affiliateUrl: string
  category: string
  brand?: string
  availability: string
  prime: boolean
  shipping: string
  sku?: string
  stock?: number
  level?: string
  duration?: string
  format?: string
  instructor?: string
}

export interface AmazonSearchParams {
  keywords: string
  category?: string
  minPrice?: number
  maxPrice?: number
  sortBy?: 'relevance' | 'price-low-to-high' | 'price-high-to-low' | 'customer-reviews' | 'price-low' | 'price-high' | 'rating'
  page?: number
  maxResults?: number
}

export interface CreatorsApiCredentials {
  clientId?: string
  clientSecret?: string
  partnerTag?: string
  apiKey?: string
  secretKey?: string
  associateTag?: string
  region?: string
  marketplace?: string
}

export interface AmazonSearchResult {
  products: AmazonProduct[]
  error: string
}

class AmazonService {
  private clientId = ''
  private clientSecret = ''
  private partnerTag = ''
  private region = 'NA'
  private marketplace = 'www.amazon.com'
  private secretOnServer = false
  lastError = ''

  initialize(credentials: CreatorsApiCredentials) {
    this.clientId = credentials.clientId || credentials.apiKey || ''
    this.clientSecret = credentials.clientSecret || credentials.secretKey || ''
    this.partnerTag = credentials.partnerTag || credentials.associateTag || ''
    this.region = credentials.region || 'NA'
    this.marketplace = credentials.marketplace || 'www.amazon.com'
  }

  markSecretOnServer(saved: boolean) {
    this.secretOnServer = saved
  }

  isConfigured(): boolean {
    return !!(this.clientId && this.partnerTag && (this.clientSecret || this.secretOnServer))
  }

  getConfigurationStatus() {
    const clientId = !!this.clientId
    const clientSecret = !!(this.clientSecret || this.secretOnServer)
    const partnerTag = !!this.partnerTag
    return {
      clientId,
      clientSecret,
      partnerTag,
      apiKey: clientId,
      secretKey: clientSecret,
      associateTag: partnerTag,
      isConfigured: this.isConfigured()
    }
  }

  generateAffiliateLink(asin: string): string {
    const tag = this.partnerTag || 'oilbuddy-20'
    const host = this.marketplace || 'www.amazon.com'
    return `https://${host}/dp/${asin}?tag=${tag}`
  }

  getCategories(): string[] {
    return ['Safety Equipment', 'Tools', 'Work Clothing', 'Electronics', 'Training']
  }

  private authHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('adminToken') || ''
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  }

  async searchProducts(searchParams: AmazonSearchParams): Promise<AmazonProduct[]> {
    const result = await this.searchCatalog(searchParams)
    return result.products
  }

  async getOilfieldProducts(params: AmazonSearchParams = { keywords: 'oilfield safety equipment' }): Promise<AmazonProduct[]> {
    return this.searchProducts(params)
  }

  async getProductById(productId: string): Promise<AmazonProduct | null> {
    const products = await this.searchProducts({ keywords: productId })
    return products.find((product) => product.id === productId) || null
  }

  async searchCatalog(params: AmazonSearchParams): Promise<AmazonSearchResult> {
    this.lastError = ''
    try {
      const response = await fetch(buildApiUrl('store/amazon'), {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({
          keywords: params.keywords || '',
          category: params.category || '',
          sortBy: params.sortBy || 'relevance',
          maxResults: params.maxResults || 10
        })
      })
      const data = await response.json().catch(() => ({}))
      const products = Array.isArray(data.products) ? data.products : []
      if (!response.ok || data.success === false) {
        this.lastError = data.error || data.message || 'Amazon catalog is unavailable.'
        return { products, error: this.lastError }
      }
      return { products, error: '' }
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : 'Amazon catalog is unavailable.'
      return { products: [], error: this.lastError }
    }
  }

  async testConnection(): Promise<{ ok: boolean; message: string }> {
    const token = localStorage.getItem('adminToken') || ''
    const response = await fetch(buildApiUrl('admin/amazon/test'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        clientId: this.clientId,
        clientSecret: this.clientSecret === '••••••••' ? '' : this.clientSecret,
        partnerTag: this.partnerTag,
        marketplace: this.marketplace
      })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      return { ok: false, message: data.error || data.message || 'Creators API connection failed.' }
    }
    return { ok: true, message: data.message || 'Creators API token request succeeded.' }
  }
}

export const amazonService = new AmazonService()
